<template>
  <config-provider :locale="currentLocale" :renderEmpty="renderEmpty">
    <router-view :key="userId" :class="$style.root" />
  </config-provider>
</template>
<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import { ConfigProvider } from 'ant-design-vue';
import zh_CN from 'ant-design-vue/lib/locale-provider/zh_CN';
import zh_TW from 'ant-design-vue/lib/locale-provider/zh_TW';
import en_US from 'ant-design-vue/lib/locale-provider/en_US';
import { ThemeSelect } from '@triascloud/x-blocks';
import { globalSocket } from '@triascloud/message-hub';
import { ssoClient } from '@triascloud/sso-client';
import { createModal, Icon as XIcon } from '@triascloud/x-components';
import { crossStorageModule, globalModule } from '@/enum/store.js';
import { TenantCenterSocket } from '@/enum/socket.js';
import { initFlexible } from '@/assets/js/flex.js';
import VerticalHorizontal from '@/components/vertical-horizontal';
import EmptyContent from '@/components/empty-content';

initFlexible();

@Component({
  components: { ConfigProvider },
})
export default class App extends Vue {
  @crossStorageModule.State user;
  @crossStorageModule.State skin;
  @crossStorageModule.State locale;
  @globalModule.Action refreshUser;
  @globalModule.Action refreshCompanyList;

  get userId() {
    return this.user && this.user.pkId;
  }

  @Watch('userId')
  handleUserChange() {
    this.destroy();
    this.init();
  }

  get localeMap() {
    return {
      zh_CN,
      zh_TW,
      en_US,
    };
  }

  get currentLocale() {
    return this.localeMap[this.locale] || zh_CN;
  }

  renderEmpty() {
    return <EmptyContent type="list" />;
  }

  @Watch('locale', { immediate: true })
  setLocaleAttr() {
    const matchLang = /^(.*?)_/.exec(this.locale);
    document.documentElement.setAttribute(
      'lang',
      matchLang ? matchLang[1] : 'zh',
    );
  }

  init() {
    VerticalHorizontal();
    ThemeSelect.setTheme(document.documentElement, this.skin);
    globalSocket.on(TenantCenterSocket.MemberUpdate, this.handleMemberUpdate);
    globalSocket.on(TenantCenterSocket.MemberDisable, this.handleMemberDisable);
    globalSocket.on(TenantCenterSocket.MemberResign, this.handleMemberResign);
    globalSocket.on(TenantCenterSocket.TenantDisband, this.handleTenantDisband);
    globalSocket.on(TenantCenterSocket.TenantUpdate, this.refreshCompanyList);
    globalSocket.on(
      TenantCenterSocket.PasswordUpdate,
      this.handlePasswordUpdate,
    );
    this.refreshCompanyList();
  }

  destroy() {
    globalSocket.off(TenantCenterSocket.MemberUpdate, this.handleMemberUpdate);
    globalSocket.off(
      TenantCenterSocket.MemberDisable,
      this.handleMemberDisable,
    );
    globalSocket.off(TenantCenterSocket.MemberResign, this.handleMemberResign);
    globalSocket.off(
      TenantCenterSocket.TenantDisband,
      this.handleTenantDisband,
    );
    globalSocket.off(TenantCenterSocket.TenantUpdate, this.refreshCompanyList);
    globalSocket.off(
      TenantCenterSocket.PasswordUpdate,
      this.handlePasswordUpdate,
    );
  }

  handleMemberUpdate() {
    this.refreshUser();
  }

  handleMemberDisable() {
    this.stopAuth('您当前所在的企业账号已经被管理员停用，请重新登录');
  }

  handleMemberResign() {
    this.stopAuth('您当前所在的企业账号已经被管理员设为离职，请重新登录');
  }

  handleTenantDisband() {
    this.stopAuth('您当前所在的企业已被企业主解散，请重新登录');
  }

  handleTenantUpdate() {
    this.refreshCompanyList();
  }

  handlePasswordUpdate() {
    ssoClient.logout(true);
  }

  stopAuth(tips) {
    createModal(
      () => (
        <div class={this.$style.tip}>
          <XIcon type="tc-icon-warning" class={this.$style.tipIcon} />
          <p>{tips}</p>
        </div>
      ),
      {
        title: this.$t('common.tips.prompt'),
        width: 440,
        className: this.$style['logout-modal'],
        closable: false,
        maskClosable: false,
        footer: () => (
          <Button type="primary" onClick={() => ssoClient.logout()}>
            {this.$t('common.action.confirm')}
          </Button>
        ),
      },
    );
  }

  get documentTitle() {
    return this.$path(this.$route.path);
  }

  get siteName() {
    return this.$t('enterprise.triascloud') === 'enterprise.triascloud'
      ? ''
      : this.$t('enterprise.triascloud');
  }

  @Watch('documentTitle', { immediate: true })
  handleDocumentTitleChange(title) {
    document.title = title ? `${title} - ${this.siteName}` : this.siteName;
  }

  created() {
    if (ssoClient.status && ssoClient.getTokenSync()) {
      this.init();
    }
  }

  beforeDestroy() {
    this.destroy();
  }
}
</script>
<style lang="less" module>
.root {
  height: 100%;
  min-width: 1200px;
}
.logout-modal {
  text-align: center;
  .tipIcon {
    font-size: 40px;
    margin-top: 10px;
    margin-bottom: 30px;
    color: #fe5959;
  }
  p {
    font-size: 16px;
    color: var(--font);
  }
}
</style>
